/**
 * 화면 경로 조립 — 문자열을 화면마다 손으로 만들지 않는다(검출기 ⑦).
 * 시뮬레이션 결과 경로에 `view` 가 빠지면 SimulationPage 가 시뮬레이션 뷰로 열려 저장된 결과가 보이지 않는다
 * (모델 홈 이력 행이 그랬다, #390 R5) — 결과로 가는 경로는 반드시 view 를 싣는다.
 */

import type { SimulationEntry } from '@/types/project';

/** 시뮬레이션 화면의 경로 — 라우터·이어보기 주소·결과 진입·화면 제목이 같은 선언을 읽는다 */
export const SIMULATION_RUN_PATH = '/simulation';

/**
 * 실행 화면 주소의 질의 이름 — 주소를 짓는 쪽(`simulationRunPath`·`simulationResultPath`)과 읽는 쪽(`readSimulationRunAddress`)이 이것 하나를
 * 쓴다(D-103: 전에는 편집기·모델 홈·프로젝트 홈·퀵 실행이 주소를 손으로 짓고, 실행 화면이 이름을 따로 읽었다)
 */
const RUN_QUERY = { projectId: 'projectId', modelId: 'modelId', simulationId: 'simulationId', view: 'view' } as const;

/** 실행 화면 주소에서 읽은 값 — 없는 질의는 null */
export interface SimulationRunAddress {
  projectId: string | null;
  /** 있으면 그 모델의 단독 화면, 없으면 프로젝트 문맥(커플링을 포함) */
  modelId: string | null;
  simulationId: string | null;
  view: string | null;
}

/** 실행 화면 주소의 질의를 읽는다 */
export const readSimulationRunAddress = (params: URLSearchParams): SimulationRunAddress => ({
  projectId: params.get(RUN_QUERY.projectId),
  modelId: params.get(RUN_QUERY.modelId),
  simulationId: params.get(RUN_QUERY.simulationId),
  view: params.get(RUN_QUERY.view),
});

/** 그 경로가 실행 화면인가 — 질의·하위 경로를 붙인 꼴 포함 */
export const isSimulationRunPath = (path: string): boolean => {
  const bare = path.split(/[?#]/)[0];
  return bare === SIMULATION_RUN_PATH || bare.startsWith(`${SIMULATION_RUN_PATH}/`);
};

/**
 * 실행 화면으로 진입 — 단독 기록·단독 세션은 그 모델의 단독 화면(`modelId`)으로. 결과 진입(아래)·이어보기(resumeRunPath)·
 * 최근 활동의 실행 중 행이 같은 조립을 쓴다. 귀속이 없으면 프로젝트 문맥 그대로.
 */
export function simulationRunPath(projectId: string, record?: { modelId?: string | null }): string {
  const model = record?.modelId ? `&${RUN_QUERY.modelId}=${encodeURIComponent(record.modelId)}` : '';
  return `${SIMULATION_RUN_PATH}?${RUN_QUERY.projectId}=${encodeURIComponent(projectId)}${model}`;
}

/**
 * 저장된 결과(이력)로 진입 — SimulationPage 가 `view` 로 분석 뷰를 연다.
 *
 * 기록의 실행 문맥도 싣는다: 단독 기록은 모델에 귀속돼 있으므로(`SimulationEntry.modelId`, 귀속 규칙은 projectManagerService 의
 * 이력 매핑 한 곳) 그 모델의 단독 화면(`modelId`)으로 연다 — 실행 화면은 주소의 modelId 로 단독·커플링을 가르므로(pageRunsCoupling)
 * 2모델 프로젝트의 단독 기록을 열어도 커플링 설정 배너·커플링 시작이 끼어들지 않는다(6차 베타 f6-04b). 귀속이 없는 기록(커플링·구 이력)은
 * 프로젝트 문맥 그대로.
 */
export function simulationResultPath(
  projectId: string,
  simulationId: string,
  view: 'history' | 'analysis' = 'history',
  record?: Pick<SimulationEntry, 'modelId'>,
): string {
  return `${simulationRunPath(projectId, record)}&${RUN_QUERY.simulationId}=${encodeURIComponent(simulationId)}&${RUN_QUERY.view}=${view}`;
}
